import type {
  OneFSAdapter,
  OneFSConfig,
  OneFSFile,
  OneFSOpenOptions,
  OneFSSaveOptions,
  OneFSDirectory,
  OneFSDirectoryOptions,
  OneFSReadDirectoryOptions,
  OneFSScanOptions,
  OneFSEntry,
  StoredHandle,
  Platform,
  OneFSResult,
  OneFSErrorCode,
  OneFSError,
  OneFSCapabilities,
  PermissionMode,
  PermissionStatus,
} from './types'
import { ok, err, PLATFORM_CAPABILITIES } from './types'

import { FSAccessAdapter } from './adapters/fs-access'
import { PickerIDBAdapter } from './adapters/picker-idb'
import { TauriAdapter } from './adapters/tauri'
import { CapacitorAdapter } from './adapters/capacitor'
import { toArrayBuffer, uint8ArrayToBase64 } from './utils'

export type {
  OneFSAdapter,
  OneFSConfig,
  OneFSFile,
  OneFSOpenOptions,
  OneFSSaveOptions,
  OneFSDirectory,
  OneFSDirectoryOptions,
  OneFSReadDirectoryOptions,
  OneFSScanOptions,
  OneFSEntry,
  StoredHandle,
  Platform,
  OneFSResult,
  OneFSErrorCode,
  OneFSError,
  OneFSCapabilities,
  PermissionMode,
  PermissionStatus,
}

export { ok, err, PLATFORM_CAPABILITIES }
export { FSAccessAdapter, PickerIDBAdapter, TauriAdapter, CapacitorAdapter }

/**
 * Cross-platform file system abstraction.
 *
 * Provides a unified API for file operations across:
 * - Modern browsers (File System Access API)
 * - Fallback browsers (file picker + IndexedDB)
 * - Tauri desktop apps
 * - Capacitor mobile apps
 *
 * @example
 * ```typescript
 * const fs = createOneFS({ appName: 'myapp' })
 *
 * // Open a file
 * const result = await fs.openFile({ accept: ['.json'] })
 * if (result.ok) {
 *   const text = fs.readAsText(result.data)
 * }
 *
 * // Check platform capabilities
 * if (fs.capabilities.canSaveInPlace) {
 *   await fs.saveFile(file, newContent)
 * } else {
 *   // Will trigger download on web-fallback
 *   await fs.saveFile(file, newContent)
 * }
 * ```
 */
export class OneFS {
  private adapter: OneFSAdapter
  private config: OneFSConfig

  constructor(config: OneFSConfig) {
    this.config = {
      maxRecentFiles: 10,
      persistByDefault: true,
      useNativeFSAccess: true,
      ...config,
    }

    this.adapter = this.selectAdapter()
  }

  private selectAdapter(): OneFSAdapter {
    const { appName, maxRecentFiles, persistByDefault, useNativeFSAccess, preferredAdapter } = this.config

    const adapters: Record<Platform, () => OneFSAdapter> = {
      tauri: () => new TauriAdapter(appName, maxRecentFiles, persistByDefault),
      capacitor: () => new CapacitorAdapter(appName, maxRecentFiles, persistByDefault),
      'web-fs-access': () => new FSAccessAdapter(appName, maxRecentFiles, persistByDefault),
      'web-fallback': () => new PickerIDBAdapter(appName, maxRecentFiles, persistByDefault),
    }

    if (preferredAdapter && adapters[preferredAdapter]) {
      const adapter = adapters[preferredAdapter]()
      if (adapter.isSupported()) return adapter
    }

    const order: Platform[] = useNativeFSAccess
      ? ['tauri', 'capacitor', 'web-fs-access', 'web-fallback']
      : ['tauri', 'capacitor', 'web-fallback']

    for (const platform of order) {
      const adapter = adapters[platform]()
      if (adapter.isSupported()) return adapter
    }

    return new PickerIDBAdapter(appName, maxRecentFiles, persistByDefault)
  }

  /** Current platform identifier */
  get platform(): Platform {
    return this.adapter.platform
  }

  /** Platform capabilities (what operations are available) */
  get capabilities(): OneFSCapabilities {
    return PLATFORM_CAPABILITIES[this.adapter.platform]
  }

  /** Whether directory operations are supported */
  get supportsDirectories(): boolean {
    return !!this.capabilities.openDirectory
  }

  /** Whether file handles can be persisted and restored across sessions */
  get supportsHandlePersistence(): boolean {
    return this.adapter.platform === 'web-fs-access'
  }

  /**
   * Open a file picker dialog.
   * @param options - Picker configuration (accept, startIn, persist)
   * @returns The selected file with content loaded as Uint8Array
   */
  async openFile(options?: OneFSOpenOptions): Promise<OneFSResult<OneFSFile>>
  async openFile(options: OneFSOpenOptions & { multiple: true }): Promise<OneFSResult<OneFSFile[]>>
  async openFile(options: OneFSOpenOptions = {}): Promise<OneFSResult<OneFSFile | OneFSFile[]>> {
    return this.adapter.openFile(options)
  }

  /**
   * Open a file picker for multiple files.
   * Convenience wrapper around openFile with multiple: true.
   */
  async openFiles(options: Omit<OneFSOpenOptions, 'multiple'> = {}): Promise<OneFSResult<OneFSFile[]>> {
    const result = await this.adapter.openFile({ ...options, multiple: true })
    if (!result.ok) return result
    const data = Array.isArray(result.data) ? result.data : [result.data]
    return ok(data)
  }

  /**
   * Save content to an existing file.
   *
   * Behavior varies by platform:
   * - web-fs-access: Saves in-place to original location
   * - tauri: Saves in-place to original location
   * - web-fallback: Triggers a download (not in-place)
   * - capacitor: Saves to app's Data directory (not original location)
   *
   * Check `capabilities.canSaveInPlace` to detect behavior.
   *
   * @param file - The file to save to (must have handle/path from openFile)
   * @param content - New content as string or Uint8Array
   * @param options - Save options (persist)
   */
  async saveFile(file: OneFSFile, content: Uint8Array | string, options?: OneFSSaveOptions): Promise<OneFSResult<boolean>> {
    return this.adapter.saveFile(file, content, options)
  }

  /**
   * Open a save dialog and write content to a new file.
   * @param content - Content to save as string or Uint8Array
   * @param options - Save options (suggestedName, accept, persist)
   * @returns The newly created file
   */
  async saveFileAs(content: Uint8Array | string, options?: OneFSSaveOptions): Promise<OneFSResult<OneFSFile>> {
    return this.adapter.saveFileAs(content, options)
  }

  /**
   * Open a directory picker dialog.
   * Not available on web-fallback platform.
   * @param options - Directory picker options (mode, persist)
   */
  async openDirectory(options?: OneFSDirectoryOptions): Promise<OneFSResult<OneFSDirectory>> {
    if (!this.adapter.openDirectory) {
      return err('not_supported', `Directory operations not supported on ${this.adapter.platform}`)
    }
    return this.adapter.openDirectory(options)
  }

  /**
   * List directory contents as entries (metadata only, no content loaded).
   * Use readFileFromDirectory() to load a specific file's content.
   *
   * @param directory - Directory from openDirectory()
   * @returns Array of file and directory entries with metadata
   */
  async readDirectory(directory: OneFSDirectory, options?: OneFSReadDirectoryOptions): Promise<OneFSResult<OneFSEntry[]>> {
    if (!this.adapter.readDirectory) {
      return err('not_supported', `Directory operations not supported on ${this.adapter.platform}`)
    }
    return this.adapter.readDirectory(directory, options)
  }

  /**
   * Load a specific file's content from a directory.
   * Use this instead of readDirectory to avoid loading all files at once.
   *
   * @param directory - Directory containing the file
   * @param entry - Entry from readDirectory() with kind === 'file'
   * @param options - Optional: maxBytes to read only first N bytes (for metadata extraction)
   * @returns The file with content loaded
   */
  async readFileFromDirectory(directory: OneFSDirectory, entry: OneFSEntry, options?: { maxBytes?: number }): Promise<OneFSResult<OneFSFile>> {
    if (!this.adapter.readFileFromDirectory) {
      return err('not_supported', `Directory operations not supported on ${this.adapter.platform}`)
    }
    return this.adapter.readFileFromDirectory(directory, entry, options)
  }

  /**
   * Recursively scan a directory for files.
   * Available on Tauri and Capacitor platforms.
   *
   * @param directory - Directory to scan
   * @param options - Scan options (extensions filter, progress callback, abort signal)
   */
  async scanDirectory(directory: OneFSDirectory, options?: OneFSScanOptions): Promise<OneFSResult<OneFSEntry[]>> {
    if (!this.adapter.scanDirectory) {
      return err('not_supported', `Recursive scanning not supported on ${this.adapter.platform}`)
    }
    return this.adapter.scanDirectory(directory, options)
  }

  /**
   * Get an efficient streaming URL for a directory entry without loading content.
   * Available on Tauri and Capacitor platforms. Use for audio/video where you don't need file in memory.
   *
   * @param entry - Entry from readDirectory() or scanDirectory()
   * @returns Asset URL or null if not supported/available
   */
  async getEntryUrl(entry: OneFSEntry): Promise<OneFSResult<string>> {
    if (!this.adapter.getEntryUrl) {
      return err('not_supported', `getEntryUrl not supported on ${this.adapter.platform}`)
    }
    try {
      const url = await this.adapter.getEntryUrl(entry)
      if (!url) {
        return err('not_found', 'No URL available for entry')
      }
      return ok(url)
    } catch (e) {
      return err('io_error', 'Failed to get entry URL', e)
    }
  }

  /**
   * Get an efficient URL for a file without re-reading content.
   * Available on Tauri and Capacitor. Falls back to blob URL.
   *
   * @param file - File to get URL for
   */
  async getFileUrl(file: OneFSFile): Promise<OneFSResult<string>> {
    if (!this.adapter.getFileUrl) {
      return err('not_supported', `getFileUrl not supported on ${this.adapter.platform}`)
    }
    try {
      const url = await this.adapter.getFileUrl(file)
      return ok(url)
    } catch (e) {
      return err('io_error', 'Failed to get file URL', e)
    }
  }

  /**
   * Get list of recently opened files.
   * On web-fs-access, these can be restored without picker.
   * On other platforms, content is restored from IndexedDB cache.
   */
  async getRecentFiles(): Promise<StoredHandle[]> {
    return this.adapter.getRecentFiles()
  }

  /**
   * Restore a previously opened file.
   * On web-fs-access: Re-reads from disk (may request permission)
   * On other platforms: Returns cached content from IndexedDB
   *
   * @param stored - Handle from getRecentFiles()
   */
  async restoreFile(stored: StoredHandle): Promise<OneFSResult<OneFSFile>> {
    return this.adapter.restoreFile(stored)
  }

  /**
   * Restore a previously opened directory.
   * Only available on web-fs-access platform.
   *
   * @param stored - Handle from getRecentFiles() with type === 'directory'
   * @param mode - Permission mode to request ('read' or 'readwrite')
   */
  async restoreDirectory(stored: StoredHandle, mode?: PermissionMode): Promise<OneFSResult<OneFSDirectory>> {
    if (!this.adapter.restoreDirectory) {
      return err('not_supported', `Directory restoration not supported on ${this.adapter.platform}`)
    }
    return this.adapter.restoreDirectory(stored, mode)
  }

  /**
   * Check current permission status on a file or directory.
   * Only available on web-fs-access platform - returns 'granted' on others.
   *
   * @param target - File or directory to check
   * @param mode - Permission mode to check ('read' or 'readwrite')
   */
  async queryPermission(target: OneFSFile | OneFSDirectory, mode: PermissionMode): Promise<PermissionStatus> {
    if (!this.adapter.queryPermission) {
      return 'granted'
    }
    return this.adapter.queryPermission(target, mode)
  }

  /**
   * Request permission on a file or directory.
   * Only available on web-fs-access platform - returns ok(true) on others.
   *
   * @param target - File or directory to request permission for
   * @param mode - Permission mode to request ('read' or 'readwrite')
   */
  async requestPermission(target: OneFSFile | OneFSDirectory, mode: PermissionMode): Promise<OneFSResult<boolean>> {
    if (!this.adapter.requestPermission) {
      return ok(true)
    }
    return this.adapter.requestPermission(target, mode)
  }

  /**
   * Store a directory by a named key (separate from recent files).
   * Useful for app preferences like "output directory".
   * Only available on web-fs-access platform.
   *
   * @param key - Unique key to store the directory under
   * @param directory - Directory to persist
   */
  async setNamedDirectory(key: string, directory: OneFSDirectory): Promise<OneFSResult<boolean>> {
    if (!this.adapter.setNamedDirectory) {
      return err('not_supported', `Named directory storage not supported on ${this.adapter.platform}`)
    }
    return this.adapter.setNamedDirectory(key, directory)
  }

  /**
   * Retrieve a previously stored named directory.
   * Only available on web-fs-access platform.
   *
   * @param key - Key the directory was stored under
   * @param mode - Permission mode to request ('read' or 'readwrite')
   */
  async getNamedDirectory(key: string, mode?: PermissionMode): Promise<OneFSResult<OneFSDirectory>> {
    if (!this.adapter.getNamedDirectory) {
      return err('not_supported', `Named directory storage not supported on ${this.adapter.platform}`)
    }
    return this.adapter.getNamedDirectory(key, mode)
  }

  /**
   * Remove a named directory from storage.
   *
   * @param key - Key the directory was stored under
   */
  async removeNamedDirectory(key: string): Promise<void> {
    if (!this.adapter.removeNamedDirectory) return
    return this.adapter.removeNamedDirectory(key)
  }

  /**
   * Delete a file and remove it from storage.
   * Available on web-fs-access, Tauri, and Capacitor platforms.
   */
  async deleteFile(file: OneFSFile): Promise<OneFSResult<boolean>> {
    if (!this.adapter.deleteFile) {
      return err('not_supported', `deleteFile not supported on ${this.adapter.platform}`)
    }
    try {
      return await this.adapter.deleteFile(file)
    } catch (e) {
      return err('io_error', 'Failed to delete file', e)
    }
  }

  /**
   * Rename a file and update storage.
   * Available on web-fs-access, Tauri, and Capacitor platforms.
   */
  async renameFile(file: OneFSFile, newName: string): Promise<OneFSResult<OneFSFile>> {
    if (!this.adapter.renameFile) {
      return err('not_supported', `renameFile not supported on ${this.adapter.platform}`)
    }
    if (!newName || typeof newName !== 'string') {
      return err('io_error', 'New name must be a non-empty string')
    }
    try {
      return await this.adapter.renameFile(file, newName)
    } catch (e) {
      return err('io_error', 'Failed to rename file', e)
    }
  }

  /**
   * Remove a file from the recent files list.
   */
  async removeFromRecent(id: string): Promise<void> {
    return this.adapter.removeFromRecent(id)
  }

  /**
   * Clear all recent files.
   */
  async clearRecent(): Promise<void> {
    return this.adapter.clearRecent()
  }

  dispose(): void {
    this.adapter.dispose?.()
  }

  // ─────────────────────────────────────────────────────────────
  // Content conversion helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Read file content as UTF-8 string.
   */
  readAsText(file: OneFSFile): string {
    return new TextDecoder().decode(file.content)
  }

  /**
   * Read file content as parsed JSON.
   * Returns an error result if content is not valid JSON.
   */
  readAsJSON<T = unknown>(file: OneFSFile): OneFSResult<T> {
    try {
      return ok(JSON.parse(new TextDecoder().decode(file.content)))
    } catch (e) {
      return err('io_error', 'Failed to parse JSON', e)
    }
  }

  /**
   * Read file content as data URL (data:mime;base64,...).
   */
  readAsDataURL(file: OneFSFile): string {
    return `data:${file.mimeType};base64,${uint8ArrayToBase64(file.content)}`
  }

  /**
   * Read file content as Blob.
   */
  readAsBlob(file: OneFSFile): Blob {
    return new Blob([toArrayBuffer(file.content)], { type: file.mimeType })
  }

  /**
   * Read file content as object URL (blob:...).
   * Remember to call URL.revokeObjectURL() when done.
   */
  readAsObjectURL(file: OneFSFile): string {
    return URL.createObjectURL(this.readAsBlob(file))
  }
}

/**
 * Create a new OneFS instance.
 *
 * @param config - Configuration options
 * @returns OneFS instance configured for the current platform
 *
 * @example
 * ```typescript
 * const fs = createOneFS({
 *   appName: 'myapp',
 *   maxRecentFiles: 20,
 *   persistByDefault: true,
 * })
 * ```
 */
export function createOneFS(config: OneFSConfig): OneFS {
  return new OneFS(config)
}
